import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import styles from './UserSection.module.scss';
import {
  Button,
  createToast,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Modal,
  ModalBody,
  ModalHeader,
  Toast,
} from 'components/core';
import { Inline, Stack } from 'components/layout';
import { Reviewer } from 'components/shared/PrimaryNavigation/PrimaryNavigation';
import { updateProfile } from 'query/reviewer';
import { PartyAvatar } from 'components/shared';
import PlanBadge from 'components/shared/PrimaryNavigation/components/ProfileDropdown/components/PlanBadge';

type Props = {
  reviewer: Reviewer;
};

type EditProfileForm = {
  firstName: string;
  lastName: string;
};

type EditProfileModalProps = {
  isModalOpen: boolean;
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  reviewer: Reviewer;
};

const reviewerName = (reviewer: Reviewer) =>
  `${reviewer?.first_name} ${reviewer?.last_name}`;

const EditProfileModal = ({
  isModalOpen,
  setModalOpen,
  reviewer,
}: EditProfileModalProps) => {
  const { formState, handleSubmit, register } = useForm<EditProfileForm>({
    mode: 'onSubmit',
    defaultValues: {
      firstName: reviewer?.first_name,
      lastName: reviewer?.last_name,
    },
  });

  const onSubmit = async ({ firstName, lastName }: EditProfileForm) => {
    return updateProfile(firstName, lastName).then((response: Response) => {
      if (response.type === 'opaqueredirect') {
        window.location.href = `${response.url}?profile_updated=true`;
      } else {
        createToast(
          <Toast variant="solid" type="danger">
            An unknown error has occurred, please try again or refresh the page.
          </Toast>
        );
      }
    });
  };

  return (
    <Modal isOpen={isModalOpen} onClose={() => setModalOpen(false)}>
      <ModalHeader>Edit profile name</ModalHeader>
      <ModalBody>
        <form
          onSubmit={handleSubmit(onSubmit)}
          aria-live="polite"
          noValidate // Suppress browser default validations as the form is using custom validations
        >
          <Stack spacing={3} className={styles.FooterStack}>
            <FormControl
              error={formState.errors.firstName?.message}
              className={styles.NoteInput}
            >
              <FormLabel>First name</FormLabel>
              <Input
                {...register('firstName', {
                  required: 'Please enter a first name',
                })}
              />
            </FormControl>
            <FormControl
              error={formState.errors.lastName?.message}
              className={styles.NoteInput}
            >
              <FormLabel>Last name</FormLabel>
              <Input
                {...register('lastName', {
                  required: 'Please enter a last name',
                })}
              />
            </FormControl>
            <Inline alignX="right" spacing={3}>
              <Button
                type="neutral"
                variant="outline"
                disabled={formState.isSubmitting}
                htmlType="button"
                onClick={() => {
                  setModalOpen(false);
                }}
              >
                Cancel
              </Button>
              <Button
                type="brand"
                disabled={formState.isSubmitting}
                isLoading={formState.isSubmitting}
                htmlType="submit"
              >
                Save changes
              </Button>
            </Inline>
          </Stack>
        </form>
      </ModalBody>
    </Modal>
  );
};

const UserSection = ({ reviewer }: Props) => {
  const [isModalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    const url = new URL(window.location.href);

    if (url.search.includes('profile_updated=true')) {
      url.searchParams.delete('profile_updated');
      history.replaceState(history.state, '', url.href);
      createToast(
        <Toast variant="solid" type="success">
          Successfully updated profile name
        </Toast>
      );
    }
  }, []);

  const onEditProfile = () => {
    setModalOpen(true);
  };

  return (
    <>
      <Inline alignX="spaceBetween" spacing={3}>
        <Inline alignX="left" spacing={3} shouldWrap={false}>
          <PartyAvatar name={reviewerName(reviewer)} />
          <Stack spacing={1} className={styles.UserDetailsContainer}>
            <Heading type="h3">{reviewerName(reviewer)}</Heading>
            <PlanBadge plan={reviewer.account.plan} />
          </Stack>
        </Inline>
        <Button
          variant="outline"
          className={styles.EditProfileButton}
          onClick={onEditProfile}
        >
          Edit profile name
        </Button>
      </Inline>
      {isModalOpen && (
        <EditProfileModal
          isModalOpen={isModalOpen}
          setModalOpen={setModalOpen}
          reviewer={reviewer}
        />
      )}
    </>
  );
};

export { UserSection };
